<template>
  <span>
    <b-button @click="show" variant="warning">Save as image</b-button>
    <b-modal
      title="Save as image"
      v-model="showModal"
      size="xl"
      ok-only
      ok-title="Done"
    >
      <div>
        <b-form-row>
          <b-col>
            <b-form-group label="Output type">
              <b-form-radio-group v-model="isZip" stacked>
                <b-form-radio :value="false">Single image file</b-form-radio>
                <b-form-radio :value="true"
                  >ZIP of multiple image files</b-form-radio
                >
              </b-form-radio-group>
            </b-form-group>

            <b-form-group label="Image format">
              <b-form-radio-group v-model="isSvg" stacked>
                <b-form-radio :value="false">PNG</b-form-radio>
                <b-form-radio :value="true">SVG</b-form-radio>
              </b-form-radio-group>
            </b-form-group>

            <b-form-group
              label="Include each card:"
              :description="includeDuplicatesDescription"
              v-if="uniqueCards"
            >
              <b-form-radio-group v-model="includeDuplicates" stacked>
                <b-form-radio :value="true"
                  >Once for each copy in the deck ({{ totalDeckCards }}
                  {{ cardOrCards(totalDeckCards) }})</b-form-radio
                >
                <b-form-radio :value="false"
                  >Once for each unique card design ({{ uniqueCards }}
                  {{ cardOrCards(uniqueCards) }})</b-form-radio
                >
              </b-form-radio-group>
            </b-form-group>
            <b-form-group label="Include each deck card:" v-else disabled>
              <b-form-radio-group>
                <b-form-radio
                  >Once for each copy in the deck (0 cards)</b-form-radio
                >
                <b-form-radio
                  >Once for each unique card design (0 cards)</b-form-radio
                >
              </b-form-radio-group>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox
                v-model="includeCharacterCards"
                :disabled="totalCards === 1"
              >
                Include {{ totalCharacters }} character
                {{ cardOrCards(totalCharacters) }}
              </b-form-checkbox>

              <b-form-checkbox v-if="totalRules" v-model="includeRuleCards">
                Include {{ totalRules }} rule {{ cardOrCards(totalRules) }}
              </b-form-checkbox>
              <b-form-checkbox v-else disabled> No rule cards </b-form-checkbox>

              <b-form-checkbox
                v-if="deck.appearance.cardbackUrl"
                v-model="includeCardBack"
              >
                Include card back
              </b-form-checkbox>
              <b-form-checkbox v-else disabled> No card back </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col>
            <b-form-group>
              <b-form-checkbox v-model="roundedCorners">
                Rounded corners
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col>
            <b-form-group
              label="Card image width"
              description="Width in pixels of each card"
              :disabled="isSvg"
            >
              <b-form-input
                v-model="imgWidth"
                number
                type="number"
                min="1"
                max="2048"
                trim
                debounce="500"
                :placeholder="isSvg ? '63mm' : ''"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="Card image height"
              description="Height in pixels of each card"
              :disabled="isSvg"
            >
              <b-form-input
                v-model="imgHeight"
                number
                readonly
                type="number"
                min="1"
                trim
                debounce="500"
                :placeholder="isSvg ? '88mm' : ''"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="DPI"
              description="Approximate"
              :disabled="isSvg"
            >
              <b-form-input
                v-model="dpi"
                number
                type="number"
                min="50"
                step="50"
                max="800"
                trim
                debounce="500"
                :placeholder="isSvg ? notApplicable : ''"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="Bleed"
              description="Padding around each card edge for print"
            >
              <b-form-input
                v-model="userBleed"
                number
                type="number"
                min="0"
                trim
                debounce="500"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col>
            <b-form-group label="Columns" :disabled="isZip">
              <b-form-input
                v-model="cols"
                number
                type="number"
                min="1"
                max="50"
                trim
                debounce="500"
                :placeholder="isZip ? notApplicable : ''"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Rows" :disabled="isZip">
              <b-form-input
                v-model="rows"
                number
                type="number"
                min="1"
                trim
                readonly
                debounce="500"
                :placeholder="isZip ? notApplicable : ''"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="Output width"
              :disabled="isZip || isSvg"
              description="Includes bleed"
            >
              <b-form-input
                :value="totalWidth"
                number
                type="number"
                min="1"
                trim
                readonly
                debounce="500"
                :placeholder="isZip || isSvg ? notApplicable : ''"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="Output height"
              :disabled="isZip || isSvg"
              description="Includes bleed"
            >
              <b-form-input
                :value="totalHeight"
                type="number"
                number
                min="1"
                trim
                readonly
                debounce="500"
                :placeholder="isZip || isSvg ? notApplicable : ''"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="3">
            <b-form-group label="Language">
              <language-picker v-model="language"></language-picker>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-alert
          show
          variant="warning"
          v-for="(warning, idx) in warnings"
          :key="`warning-message-${idx}`"
          text-variant="warning"
        >
          {{ warning }}
        </b-alert>

        <!-- <b-form-row>
          <b-col>
            <b-form-group label="Output filename">
              <b-form-input
                v-model="userFilename"
                :placeholder="filenamePlaceholder"
                trim
                debounce="500"
              />
            </b-form-group>
          </b-col>
        </b-form-row> -->
        <!-- <p>
          {{summaryText}}
        </p> -->

        <b-button
          :href="downloadLink"
          :download="filename"
          :disabled="!totalCards"
          >Download</b-button
        >
        <b-form-text v-if="totalCards" text-variant="info">
          It may take some time for your download to start while the server
          creates your file.
        </b-form-text>
        <b-form-text v-else text-variant="danger">
          Please select something to output
        </b-form-text>
      </div>
    </b-modal>
  </span>
</template>

<script type="text/javascript">
import api from "@/api";
import checkEmoji from "@/mixins/checkEmoji.js";
import LanguagePicker from "@/components/LanguagePicker.vue"

export default {
  name: "UnmatchedImageExporter",
  components: {LanguagePicker},
  props: {
    versionID: {
      type: String,
      required: true,
    },
    deck: {
      type: Object,
      required: true,
    },
    deckName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isZip: false,
      isSvg: false,
      includeDuplicates: false,
      showModal: false,
      cardWidth: 63,
      cardHeight: 88,
      userCols: 6,
      userImgWidth: 372,
      userBleed: 0,
      includeCharacterCards: true,
      includeDeckCards: true,
      includeRuleCards: true,
      includeCardBack: true,
      roundedCorners: true,
      userFilename: "",
      notApplicable: "N/A",
      language: "en",    
    };
  },
  asyncComputed: {
    async cardImageErrors() {
      const deckImageErrors = await api.checkDeckImages(this.versionID);
      return deckImageErrors;
    },
  },
  computed: {
    uniqueCards() {
      return this.deck.cards.length;
    },
    totalCharacters() {
      return 1 + this.deck.extraCharacters.length;
    },
    totalRules() {
      return this.deck.ruleCards.length;
    },
    cols: {
      set(value) {
        this.userCols = value;
      },
      get() {
        return this.isZip ? null : this.userCols;
      },
    },
    rows() {
      return this.isZip ? null : Math.ceil(this.totalCards / this.cols);
    },
    imgWidth: {
      set(value) {
        this.userImgWidth = parseInt(value);
      },
      get() {
        return this.isSvg ? null : this.userImgWidth;
      },
    },
    imgHeight() {
      return this.isSvg
        ? null
        : Math.ceil((this.cardHeight / this.cardWidth) * this.imgWidth);
    },
    totalWidth() {
      const nominalCols = this.isZip ? 1 : this.cols;
      return (
        (this.isSvg
          ? this.cardWidth + 2 * this.bleedMm
          : this.imgWidth + 2 * this.userBleed) * nominalCols
      );
    },
    totalHeight() {
      const nominalRows = this.isZip ? 1 : this.rows;
      return (
        (this.isSvg
          ? this.cardHeight + 2 * this.bleedMm
          : this.imgHeight + 2 * this.userBleed) * nominalRows
      );
    },
    dpi: {
      get() {
        return this.isSvg
          ? null
          : Math.round((25.4 * this.imgWidth) / this.cardWidth);
      },
      set(value) {
        this.imgWidth = Math.round((value * this.cardWidth) / 25.4);
      },
    },
    bleedMm: {
      get() {
        return this.isSvg
          ? this.userBleed
          : +(this.userBleed * (this.cardWidth / this.imgWidth)).toFixed(2);
      },
    },
    downloadLink() {
      const format = this.isSvg ? "svg" : "png";
      const url = new URL(
        `/api/card_img/${this.versionID}`,
        window.location.origin
      );
      if (this.imgWidth) url.searchParams.append("width", this.imgWidth);
      if (this.cols) url.searchParams.append("columns", this.cols);
      if (this.bleedMm) url.searchParams.append("bleed", this.bleedMm);
      url.searchParams.append("rounded_corners", this.roundedCorners);
      url.searchParams.append("characters", this.includeCharacterCards);
      url.searchParams.append("rules", this.includeRuleCards);
      url.searchParams.append("cardback", this.includeCardBack);
      url.searchParams.append("duplicates", this.includeDuplicates);
      url.searchParams.append("zip", this.isZip);
      url.searchParams.append("format", format);
      url.searchParams.append("language", this.language);

      return url.href;
      // return `/api/card_img/${this.versionID}?width=${this.imgWidth}&columns=${this.cols}&characters=${this.includeCharacterCards}&rules=${this.includeRuleCards}&duplicates=${this.includeDuplicates}&zip=${this.isZip}&format=${format}`;
    },
    includeDuplicatesDescription() {
      return this.isZip && this.includeDuplicates
        ? "Usually you'll want 'unique design' when using the ZIP option, but hey, it's up to you."
        : "";
    },
    filename() {
      const extension = this.isZip ? ".zip" : this.isSvg ? ".svg" : ".png";
      var filename =
        this.userFilename || this.deckName || `unmatched_deck${extension}`;
      if (!filename.toLocaleLowerCase().endsWith(extension))
        filename += extension;
      return filename;
    },
    filenamePlaceholder() {
      const extension = this.isZip ? ".zip" : this.isSvg ? ".svg" : ".png";
      var filename = this.deckName || `unmatched_deck${extension}`;
      return filename;
    },
    totalCards() {
      var totalCards = 0;
      totalCards += this.includeDeckCards
        ? this.includeDuplicates
          ? this.totalDeckCards
          : this.uniqueCards
        : 0;
      totalCards += this.includeCharacterCards ? this.totalCharacters : 0;
      totalCards += this.includeRuleCards ? this.totalRules : 0;
      totalCards +=
        this.includeCardBack && this.deck.appearance.cardbackUrl ? 1 : 0;

      return totalCards;
    },
    totalDeckCards() {
      const reducer = (acc, card) => acc + card.quantity;
      return this.deck.cards.reduce(reducer, 0);
    },
    warnings() {
      const warnings = [];

      if (this.cols > this.totalCards) {
        warnings.push(
          "There are more columns than cards, so your image will be padded with empty space."
        );
      }

      const tooBig = 10000;
      if (this.totalWidth > tooBig || this.totalHeight > tooBig) {
        warnings.push(
          "The output image file is going to be very large. If there are problems downloading, try lowering the card width/DPI or switch to ZIP output."
        );
      }

      if (this.deck.appearance.patternName) {
        warnings.push("Card background patterns are not currently supported.");
      }

      if (this.deck.appearance.isPNP) {
        warnings.push(
          '"Print and play" appearance is not currently supported. It will export with the "Production" appearance instead.'
        );
      }

      if (
        checkEmoji(
          this.deck,
          this.includeCharacterCards,
          this.includeRuleCards,
          this.includeDeckCards
        )
      ) {
        warnings.push("Emoji will not render properly.");
      }

      if (
        this.deck.appearance.cardbackUrl &&
        this.includeCardBack &&
        this.userBleed
      ) {
        warnings.push(
          "Card backs and bleed may not behave as expected; double check your output"
        );
      }

      if (
        !this.isSvg &&
        this.includeDeckCards &&
        this.cardImageErrors?.length
      ) {
        this.cardImageErrors.forEach((error) => {
          warnings.push(
            `The image for ${error.title} could not be loaded and will not be included in output`
          );
        });
      }

      return warnings;
    },
  },
  methods: {
    show() {
      this.showModal = true;
    },
    cardOrCards(count) {
      return count === 1 ? "card" : "cards";
    },
  },
};
</script>
